/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 * Written by Hendrik, December 28, 2025.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './dashboard.css';

export default function DeveloperDashboard() {
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [schoolUsers, setSchoolUsers] = useState([]);
    const [schoolAnalytics, setSchoolAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newSchool, setNewSchool] = useState({ school_name: '', school_code: '' });
    const [newUser, setNewUser] = useState({ username: '', email: '', password: '', confirmPassword: '', role: 'teacher' });
    const [showAddSchool, setShowAddSchool] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showBulkImport, setShowBulkImport] = useState(false);
    const [activeTab, setActiveTab] = useState('users'); // users, analytics, hod-subjects
    const [editingRole, setEditingRole] = useState(null);
    const [csvFile, setCsvFile] = useState(null);

    // HOD Subject Assignment state
    const [hodSubjectData, setHodSubjectData] = useState({ assignments: [], hods: [], subjects: [] });
    const [selectedHodForAssignment, setSelectedHodForAssignment] = useState('');
    const [selectedSubjectsForAssignment, setSelectedSubjectsForAssignment] = useState([]);

    const token = localStorage.getItem('auth_token');

    const loadSchools = useCallback(async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_schools.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSchools(response.data.schools || []);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => {
        loadSchools();
    }, [loadSchools]);

    const loadSchoolUsers = async (schoolId) => {
        try {
            // Create a temporary admin context for this request by passing school_id
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/get_users.php?school_id=${schoolId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSchoolUsers(response.data.users || []);
        } catch (error) {
            setSchoolUsers([]);
        }
    };

    const loadSchoolAnalytics = async (schoolId) => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_school_analytics.php?school_id=${schoolId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setSchoolAnalytics(response.data);
        } catch (error) {
            setSchoolAnalytics(null);
        }
    };

    const loadHodSubjects = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/hod_subjects.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.success) {
                setHodSubjectData({
                    assignments: response.data.assignments || [],
                    hods: response.data.hods || [],
                    subjects: response.data.subjects || []
                });
            }
        } catch (error) {
        }
    };

    const handleAssignSubjectsToHod = async () => {
        if (!selectedHodForAssignment || selectedSubjectsForAssignment.length === 0) {
            alert('Please select an HOD and at least one subject');
            return;
        }
        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/hod_subjects.php`, {
                hod_id: parseInt(selectedHodForAssignment),
                subject_ids: selectedSubjectsForAssignment.map(id => parseInt(id))
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.success) {
                alert(response.data.message);
                setSelectedHodForAssignment('');
                setSelectedSubjectsForAssignment([]);
                loadHodSubjects();
            }
        } catch (error) {
            alert('Error assigning subjects: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleRemoveHodAssignment = async (assignmentId) => {
        if (!window.confirm('Remove this subject assignment?')) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/hod_subjects.php`, {
                headers: { 'Authorization': `Bearer ${token}` },
                data: { assignment_id: assignmentId }
            });
            loadHodSubjects();
        } catch (error) {
            alert('Error removing assignment: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleClearHodAssignments = async (hodId) => {
        if (!window.confirm('Remove ALL subject assignments for this HOD? They will see all subjects.')) return;
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}/hod_subjects.php`, {
                headers: { 'Authorization': `Bearer ${token}` },
                data: { hod_id: hodId }
            });
            loadHodSubjects();
        } catch (error) {
            alert('Error clearing assignments: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSelectSchool = async (school) => {
        setSelectedSchool(school);
        setShowAddUser(false);
        setShowBulkImport(false);
        setActiveTab('users');
        await loadSchoolUsers(school.id);
        await loadSchoolAnalytics(school.id);
        await loadHodSubjects();
    };

    const handleAddSchool = async (e) => {
        e.preventDefault();
        if (!newSchool.school_name || !newSchool.school_code) {
            alert('School name and code are required');
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/add_school.php`, newSchool, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.success) {
                alert('School added successfully!');
                setNewSchool({ school_name: '', school_code: '' });
                setShowAddSchool(false);
                loadSchools();
            }
        } catch (error) {
            alert('Error adding school: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteSchool = async (schoolId, schoolName) => {
        if (window.confirm(`Delete school "${schoolName}"? All users and data will be removed.`)) {
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/delete_school.php`,
                    { school_id: schoolId },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.data.success) {
                    alert('School deleted successfully!');
                    setSelectedSchool(null);
                    loadSchools();
                }
            } catch (error) {
                alert('Error deleting school: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!newUser.username || !newUser.password || !newUser.confirmPassword) {
            alert('Username and password are required');
            return;
        }
        if (newUser.password !== newUser.confirmPassword) {
            alert('Passwords do not match');
            return;
        }
        if (newUser.password.length < 6) {
            alert('Password must be at least 6 characters');
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/add_user.php`,
                {
                    username: newUser.username,
                    email: newUser.email || '',
                    password: newUser.password,
                    role: newUser.role,
                    school_id: selectedSchool.id
                },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (response.data.success) {
                alert('User added successfully!');
                setNewUser({ username: '', email: '', password: '', confirmPassword: '', role: 'teacher' });
                setShowAddUser(false);
                loadSchoolUsers(selectedSchool.id);
            } else {
                alert('Error: ' + (response.data.error || 'Failed to add user'));
            }
        } catch (error) {
            alert('Error adding user: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (window.confirm(`Delete user "${username}"?`)) {
            try {
                const response = await axios.post(`${process.env.REACT_APP_API_URL}/delete_user.php`,
                    { user_id: userId },
                    { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (response.data.success) {
                    alert('User deleted successfully!');
                    loadSchoolUsers(selectedSchool.id);
                }
            } catch (error) {
                alert('Error deleting user: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const handleChangePassword = async (userId, username) => {
        const newPassword = prompt(`Enter new password for user "${username}":`);
        if (!newPassword) return;

        if (newPassword.length < 6) {
            alert('Password must be at least 6 characters');
            return;
        }

        const confirmPassword = prompt('Confirm new password:');
        if (confirmPassword !== newPassword) {
            alert('Passwords do not match');
            return;
        }

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/change_password.php`,
                { user_id: userId, new_password: newPassword },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.data.success) {
                alert('Password changed successfully!');
            } else {
                alert('Error changing password: ' + (response.data.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Error changing password: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleChangeUserRole = async (userId, newRole, currentUsername) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/update_user_role.php`,
                { user_id: userId, new_role: newRole },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.data.success) {
                alert(`User role changed to ${newRole}`);
                setEditingRole(null);
                loadSchoolUsers(selectedSchool.id);
            }
        } catch (error) {
            alert('Error changing role: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleBulkUserImport = async (e) => {
        e.preventDefault();
        if (!csvFile) {
            alert('Please select a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const csv = event.target.result;
                const lines = csv.split('\n');
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

                const usernameIdx = headers.indexOf('username');
                const emailIdx = headers.indexOf('email');
                const roleIdx = headers.indexOf('role');
                const passwordIdx = headers.indexOf('password');

                if (usernameIdx === -1) {
                    alert('CSV must have a "username" column');
                    return;
                }

                let successCount = 0;
                let errorCount = 0;
                const errors = [];

                for (let i = 1; i < lines.length; i++) {
                    if (!lines[i].trim()) continue;

                    const values = lines[i].split(',').map(v => v.trim());
                    const username = values[usernameIdx];
                    const email = emailIdx >= 0 ? values[emailIdx] : '';
                    const role = roleIdx >= 0 ? values[roleIdx] : 'teacher';
                    const password = passwordIdx >= 0 ? values[passwordIdx] : username; // Use username as default password

                    try {
                        const response = await axios.post(`${process.env.REACT_APP_API_URL}/add_user.php`,
                            {
                                username,
                                email,
                                password,
                                role,
                                school_id: selectedSchool.id
                            },
                            { headers: { 'Authorization': `Bearer ${token}` } }
                        );
                        if (response.data.success) {
                            successCount++;
                        } else {
                            errorCount++;
                            errors.push(`Row ${i}: ${response.data.error}`);
                        }
                    } catch (error) {
                        errorCount++;
                        errors.push(`Row ${i}: ${error.response?.data?.error || error.message}`);
                    }
                }

                const message = `Import complete!\nSuccessfully added: ${successCount}\nErrors: ${errorCount}${errors.length > 0 ? '\n\nErrors:\n' + errors.slice(0, 5).join('\n') + (errors.length > 5 ? `\n... and ${errors.length - 5} more` : '') : ''}`;
                alert(message);
                setCsvFile(null);
                setShowBulkImport(false);
                loadSchoolUsers(selectedSchool.id);
            } catch (error) {
                alert('Error parsing CSV: ' + error.message);
            }
        };
        reader.readAsText(csvFile);
    };

    if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;

    const isMobile = window.innerWidth < 768;

    // Get personalized greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        const username = localStorage.getItem('username') || 'User';
        let greeting = '';

        if (hour < 12) {
            greeting = 'Good morning';
        } else if (hour < 17) {
            greeting = 'Good afternoon';
        } else {
            greeting = 'Good evening';
        }

        return `${greeting}, ${username}!`;
    };

    return (
        <div style={{ padding: '20px', background: 'white', minHeight: '100vh', marginLeft: isMobile ? '0' : '15%' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                <h1 style={{ marginTop: 0, fontSize: isMobile ? '24px' : '32px' }}>{getGreeting()}</h1>
                <p style={{ color: '#666', marginBottom: '30px', fontSize: isMobile ? '13px' : '14px' }}>
                    Manage all schools, their users, and view analytics. You have system-level control.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '280px 1fr', gap: isMobile ? '15px' : '30px' }}>
                    {/* Schools List */}
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: isMobile ? '15px' : '20px', backgroundColor: '#f9f9f9', height: isMobile ? 'auto' : 'fit-content' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ margin: 0, fontSize: isMobile ? '14px' : '16px' }}>Schools</h2>
                            <button
                                onClick={() => setShowAddSchool(!showAddSchool)}
                                style={{
                                    padding: isMobile ? '6px 10px' : '6px 12px',
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: isMobile ? '11px' : '12px'
                                }}
                            >
                                + Add
                            </button>
                        </div>

                        {showAddSchool && (
                            <form onSubmit={handleAddSchool} style={{ marginBottom: '15px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
                                <input
                                    type="text"
                                    placeholder="School Name"
                                    value={newSchool.school_name}
                                    onChange={(e) => setNewSchool({ ...newSchool, school_name: e.target.value })}
                                    style={{ width: '100%', padding: '6px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                />
                                <input
                                    type="text"
                                    placeholder="School Code"
                                    value={newSchool.school_code}
                                    onChange={(e) => setNewSchool({ ...newSchool, school_code: e.target.value })}
                                    style={{ width: '100%', padding: '6px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                />
                                <button type="submit" style={{ width: '100%', padding: '6px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                                    Add
                                </button>
                            </form>
                        )}

                        <div style={{ display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : undefined, flexDirection: isMobile ? undefined : 'column', gap: isMobile ? '10px' : '8px', maxHeight: isMobile ? 'none' : '500px', overflowY: isMobile ? 'visible' : 'auto' }}>
                            {schools.map(school => (
                                <div
                                    key={school.id}
                                    onClick={() => handleSelectSchool(school)}
                                    style={{
                                        padding: isMobile ? '10px' : '12px',
                                        backgroundColor: selectedSchool?.id === school.id ? '#2196F3' : '#fff',
                                        color: selectedSchool?.id === school.id ? 'white' : '#000',
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s',
                                        fontSize: isMobile ? '12px' : '13px'
                                    }}
                                >
                                    <strong>{school.school_name}</strong>
                                    <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '3px' }}>
                                        {school.school_code}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* School Details */}
                    <div>
                        {selectedSchool ? (
                            <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: isMobile ? '15px' : '20px' }}>
                                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: isMobile ? 'flex-start' : 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: '20px', gap: isMobile ? '10px' : '0' }}>
                                    <div>
                                        <h2 style={{ margin: '0 0 5px 0', fontSize: isMobile ? '18px' : '24px' }}>{selectedSchool.school_name}</h2>
                                        <p style={{ color: '#666', margin: '0', fontSize: isMobile ? '11px' : '13px' }}>
                                            Code: <strong>{selectedSchool.school_code}</strong> {!isMobile && '|'} {isMobile && <br />}Created: {new Date(selectedSchool.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleDeleteSchool(selectedSchool.id, selectedSchool.school_name)}
                                        style={{
                                            padding: isMobile ? '6px 12px' : '8px 16px',
                                            backgroundColor: '#f44336',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: isMobile ? '12px' : '13px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Delete
                                    </button>
                                </div>

                                {/* Tabs */}
                                <div style={{ display: 'flex', gap: isMobile ? '5px' : '10px', marginBottom: '20px', borderBottom: '2px solid #ddd', paddingBottom: '10px', overflowX: isMobile ? 'auto' : 'visible' }}>
                                    <button
                                        onClick={() => setActiveTab('users')}
                                        style={{
                                            padding: isMobile ? '6px 10px' : '8px 16px',
                                            backgroundColor: activeTab === 'users' ? '#2196F3' : 'transparent',
                                            color: activeTab === 'users' ? 'white' : '#666',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: activeTab === 'users' ? 'bold' : 'normal',
                                            fontSize: isMobile ? '12px' : '13px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Users ({schoolUsers.length})
                                    </button>
                                    <button
                                        onClick={() => setActiveTab('analytics')}
                                        style={{
                                            padding: isMobile ? '6px 10px' : '8px 16px',
                                            backgroundColor: activeTab === 'analytics' ? '#2196F3' : 'transparent',
                                            color: activeTab === 'analytics' ? 'white' : '#666',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: activeTab === 'analytics' ? 'bold' : 'normal',
                                            fontSize: isMobile ? '12px' : '13px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        Analytics
                                    </button>
                                    <button
                                        onClick={() => { setActiveTab('hod-subjects'); loadHodSubjects(); }}
                                        style={{
                                            padding: isMobile ? '6px 10px' : '8px 16px',
                                            backgroundColor: activeTab === 'hod-subjects' ? '#9C27B0' : 'transparent',
                                            color: activeTab === 'hod-subjects' ? 'white' : '#666',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontWeight: activeTab === 'hod-subjects' ? 'bold' : 'normal',
                                            fontSize: isMobile ? '12px' : '13px',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        HOD Subjects
                                    </button>
                                </div>

                                {/* Users Tab */}
                                {activeTab === 'users' && (
                                    <div>
                                        <div style={{ display: 'flex', gap: isMobile ? '5px' : '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                                            <button
                                                onClick={() => setShowAddUser(!showAddUser)}
                                                style={{
                                                    padding: isMobile ? '6px 10px' : '8px 16px',
                                                    backgroundColor: '#4CAF50',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: isMobile ? '12px' : '13px'
                                                }}
                                            >
                                                + Add
                                            </button>
                                            <button
                                                onClick={() => setShowBulkImport(!showBulkImport)}
                                                style={{
                                                    padding: isMobile ? '6px 10px' : '8px 16px',
                                                    backgroundColor: '#FF9800',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    fontSize: isMobile ? '12px' : '13px'
                                                }}
                                            >
                                                {isMobile ? 'Import' : 'Bulk Import'}
                                            </button>
                                        </div>

                                        {showAddUser && (
                                            <form onSubmit={handleAddUser} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px' }}>
                                                <h3 style={{ marginTop: 0, fontSize: isMobile ? '13px' : '14px' }}>Add New User</h3>
                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '10px' }}>
                                                    <input
                                                        type="text"
                                                        placeholder="Username"
                                                        value={newUser.username}
                                                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                                    />
                                                    <input
                                                        type="email"
                                                        placeholder="Email (optional)"
                                                        value={newUser.email}
                                                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                                    />
                                                    <input
                                                        type="password"
                                                        placeholder="Password"
                                                        value={newUser.password}
                                                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                                    />
                                                    <input
                                                        type="password"
                                                        placeholder="Confirm Password"
                                                        value={newUser.confirmPassword}
                                                        onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                                    />
                                                    <select
                                                        value={newUser.role}
                                                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', boxSizing: 'border-box' }}
                                                    >
                                                        <option value="teacher">Teacher</option>
                                                        <option value="admin">Admin</option>
                                                        <option value="hod">HOD</option>
                                                        <option value="developer">Developer</option>
                                                    </select>
                                                </div>
                                                <button type="submit" style={{ width: '100%', marginTop: '10px', padding: '8px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                                    Add User
                                                </button>
                                            </form>
                                        )}

                                        {showBulkImport && (
                                            <form onSubmit={handleBulkUserImport} style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3e0', borderRadius: '8px' }}>
                                                <h3 style={{ marginTop: 0, fontSize: isMobile ? '13px' : '14px' }}>Bulk Import</h3>
                                                <p style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '10px' }}>
                                                    CSV: username, email (optional), role (default: teacher), password (default: username)
                                                </p>
                                                <input
                                                    type="file"
                                                    accept=".csv"
                                                    onChange={(e) => setCsvFile(e.target.files[0])}
                                                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '12px', marginBottom: '10px', display: 'block', width: '100%', boxSizing: 'border-box' }}
                                                />
                                                <button type="submit" style={{ width: '100%', padding: '8px', backgroundColor: '#FF9800', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>
                                                    Import from CSV
                                                </button>
                                            </form>
                                        )}

                                        <div style={{ overflowX: isMobile ? 'auto' : 'visible', fontSize: isMobile ? '11px' : '12px' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '300px' : 'auto' }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'left', fontSize: isMobile ? '11px' : '12px' }}>Username</th>
                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'left', fontSize: isMobile ? '11px' : '12px' }}>Role</th>
                                                        {!isMobile && <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'left', fontSize: '12px' }}>Created</th>}
                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {schoolUsers.map(user => (
                                                        <tr key={user.user_id}>
                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', fontSize: isMobile ? '11px' : '12px' }}>{user.username}</td>
                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', fontSize: isMobile ? '11px' : '12px' }}>
                                                                {editingRole === user.user_id ? (
                                                                    <select
                                                                        value={user.role}
                                                                        onChange={(e) => {
                                                                            handleChangeUserRole(user.user_id, e.target.value, user.username);
                                                                        }}
                                                                        style={{ padding: '4px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '11px' }}
                                                                    >
                                                                        <option value="teacher">Teacher</option>
                                                                        <option value="admin">Admin</option>
                                                                        <option value="hod">HOD</option>
                                                                        <option value="developer">Developer</option>
                                                                    </select>
                                                                ) : (
                                                                    <span onClick={() => setEditingRole(user.user_id)} style={{ cursor: 'pointer', color: '#2196F3', textDecoration: 'underline' }}>
                                                                        {user.role}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            {!isMobile && <td style={{ padding: '10px', border: '1px solid #ddd', fontSize: '11px', color: '#666' }}>
                                                                {new Date(user.created_at).toLocaleDateString()}
                                                            </td>}
                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center' }}>
                                                                <button
                                                                    onClick={() => handleChangePassword(user.user_id, user.username)}
                                                                    style={{
                                                                        padding: isMobile ? '4px 6px' : '4px 8px',
                                                                        backgroundColor: '#ff9800',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '11px',
                                                                        marginRight: '4px'
                                                                    }}
                                                                >
                                                                    Change Password
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteUser(user.user_id, user.username)}
                                                                    style={{
                                                                        padding: isMobile ? '4px 6px' : '4px 8px',
                                                                        backgroundColor: '#f44336',
                                                                        color: 'white',
                                                                        border: 'none',
                                                                        borderRadius: '4px',
                                                                        cursor: 'pointer',
                                                                        fontSize: '11px'
                                                                    }}
                                                                >
                                                                    Delete
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                            {schoolUsers.length === 0 && (
                                                <p style={{ textAlign: 'center', color: '#999', padding: '20px', fontSize: isMobile ? '12px' : '13px' }}>
                                                    No users yet. Add one to get started.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Analytics Tab */}
                                {activeTab === 'analytics' && (
                                    <div>
                                        {schoolAnalytics ? (
                                            <div>
                                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '20px' }}>
                                                    <div style={{ border: '1px solid #ddd', padding: isMobile ? '12px' : '15px', borderRadius: '8px', backgroundColor: '#f0f8ff' }}>
                                                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '5px' }}>Total Users</div>
                                                        <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', color: '#2196F3' }}>
                                                            {schoolAnalytics.total_users || 0}
                                                        </div>
                                                    </div>
                                                    <div style={{ border: '1px solid #ddd', padding: isMobile ? '12px' : '15px', borderRadius: '8px', backgroundColor: '#f0fff4' }}>
                                                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '5px' }}>Total Students</div>
                                                        <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', color: '#4CAF50' }}>
                                                            {schoolAnalytics.total_students || 0}
                                                        </div>
                                                    </div>
                                                    <div style={{ border: '1px solid #ddd', padding: isMobile ? '12px' : '15px', borderRadius: '8px', backgroundColor: '#fff3e0' }}>
                                                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '5px' }}>Total Subjects</div>
                                                        <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', color: '#FF9800' }}>
                                                            {schoolAnalytics.total_subjects || 0}
                                                        </div>
                                                    </div>
                                                    <div style={{ border: '1px solid #ddd', padding: isMobile ? '12px' : '15px', borderRadius: '8px', backgroundColor: '#fce4ec' }}>
                                                        <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#666', marginBottom: '5px' }}>Avg Pass Rate</div>
                                                        <div style={{ fontSize: isMobile ? '20px' : '28px', fontWeight: 'bold', color: '#e91e63' }}>
                                                            {schoolAnalytics.avg_pass_rate ? schoolAnalytics.avg_pass_rate.toFixed(1) : 0}%
                                                        </div>
                                                    </div>
                                                </div>

                                                {schoolAnalytics.subject_stats && schoolAnalytics.subject_stats.length > 0 && (
                                                    <div style={{ marginTop: '20px', padding: isMobile ? '12px' : '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd' }}>
                                                        <h3 style={{ margin: '0 0 15px 0', fontSize: isMobile ? '13px' : '14px' }}>Subject Performance</h3>
                                                        <div style={{ overflowX: isMobile ? 'auto' : 'visible', fontSize: isMobile ? '11px' : '12px' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '300px' : 'auto' }}>
                                                                <thead>
                                                                    <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'left', fontSize: isMobile ? '11px' : '12px' }}>Subject</th>
                                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>Students</th>
                                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>Avg Mark</th>
                                                                        <th style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>Pass Rate</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {schoolAnalytics.subject_stats.map((subject, idx) => (
                                                                        <tr key={idx}>
                                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', fontSize: isMobile ? '11px' : '12px' }}>{subject.subject_name}</td>
                                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>{subject.student_count}</td>
                                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>
                                                                                {subject.avg_mark ? subject.avg_mark.toFixed(1) : '-'}
                                                                            </td>
                                                                            <td style={{ padding: isMobile ? '8px' : '10px', border: '1px solid #ddd', textAlign: 'center', fontSize: isMobile ? '11px' : '12px' }}>
                                                                                {subject.pass_rate ? subject.pass_rate.toFixed(1) : 0}%
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                )}

                                                {schoolAnalytics.user_distribution && (
                                                    <div style={{ marginTop: '20px', padding: isMobile ? '12px' : '15px', backgroundColor: '#f9f9f9', borderRadius: '8px', border: '1px solid #ddd' }}>
                                                        <h3 style={{ margin: '0 0 15px 0', fontSize: isMobile ? '13px' : '14px' }}>User Distribution</h3>
                                                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px' }}>
                                                            <div style={{ padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #bbdefb' }}>
                                                                <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#1565c0' }}>Teachers</div>
                                                                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 'bold', color: '#0d47a1' }}>
                                                                    {schoolAnalytics.user_distribution.teachers || 0}
                                                                </div>
                                                            </div>
                                                            <div style={{ padding: '10px', backgroundColor: '#f3e5f5', borderRadius: '4px', border: '1px solid #e1bee7' }}>
                                                                <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#6a1b9a' }}>Admins</div>
                                                                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 'bold', color: '#4a148c' }}>
                                                                    {schoolAnalytics.user_distribution.admins || 0}
                                                                </div>
                                                            </div>
                                                            <div style={{ padding: '10px', backgroundColor: '#fce4ec', borderRadius: '4px', border: '1px solid #f8bbd0' }}>
                                                                <div style={{ fontSize: isMobile ? '10px' : '11px', color: '#c2185b' }}>HODs</div>
                                                                <div style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 'bold', color: '#880e4f' }}>
                                                                    {schoolAnalytics.user_distribution.hods || 0}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            <p style={{ textAlign: 'center', color: '#999', padding: '40px', fontSize: '13px' }}>
                                                Loading analytics...
                                            </p>
                                        )}
                                    </div>
                                )}

                                {/* HOD Subjects Tab */}
                                {activeTab === 'hod-subjects' && (
                                    <div>
                                        <h3 style={{ margin: '0 0 20px 0', fontSize: isMobile ? '14px' : '16px' }}>HOD Subject Assignments</h3>
                                        <p style={{ fontSize: '12px', color: '#666', marginBottom: '15px' }}>
                                            Assign specific subjects to HODs. HODs with no assignments can see all subjects (Super HOD).
                                        </p>

                                        {/* Assignment Form */}
                                        <div style={{ padding: '15px', backgroundColor: '#f3e5f5', borderRadius: '8px', marginBottom: '20px' }}>
                                            <h4 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Assign Subjects to HOD</h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr auto', gap: '10px', alignItems: 'end' }}>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px' }}>Select HOD:</label>
                                                    <select
                                                        value={selectedHodForAssignment}
                                                        onChange={(e) => setSelectedHodForAssignment(e.target.value)}
                                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                                                    >
                                                        <option value="">-- Select HOD --</option>
                                                        {hodSubjectData.hods.map(hod => (
                                                            <option key={hod.id} value={hod.id}>{hod.username}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label style={{ display: 'block', fontSize: '12px', marginBottom: '5px' }}>Select Subjects:</label>
                                                    <select
                                                        multiple
                                                        value={selectedSubjectsForAssignment}
                                                        onChange={(e) => setSelectedSubjectsForAssignment(Array.from(e.target.selectedOptions, opt => opt.value))}
                                                        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', minHeight: '80px' }}
                                                    >
                                                        {hodSubjectData.subjects.map(sub => (
                                                            <option key={sub.id} value={sub.id}>Grade {sub.grade} - {sub.name}</option>
                                                        ))}
                                                    </select>
                                                    <small style={{ color: '#666', fontSize: '10px' }}>Hold Ctrl/Cmd to select multiple</small>
                                                </div>
                                                <button
                                                    onClick={handleAssignSubjectsToHod}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#9C27B0',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    Assign
                                                </button>
                                            </div>
                                        </div>

                                        {/* Current Assignments */}
                                        <div style={{ padding: '15px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #ddd' }}>
                                            <h4 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Current Assignments</h4>
                                            {hodSubjectData.hods.length === 0 ? (
                                                <p style={{ color: '#999', fontSize: '12px' }}>No HOD users found. Create an HOD user first.</p>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                                    {hodSubjectData.hods.map(hod => {
                                                        const hodAssignments = hodSubjectData.assignments.filter(a => a.hod_user_id === hod.id);
                                                        return (
                                                            <div key={hod.id} style={{ padding: '12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                                    <strong style={{ fontSize: '13px' }}>{hod.username}</strong>
                                                                    {hodAssignments.length > 0 && (
                                                                        <button
                                                                            onClick={() => handleClearHodAssignments(hod.id)}
                                                                            style={{
                                                                                padding: '4px 8px',
                                                                                backgroundColor: '#f44336',
                                                                                color: 'white',
                                                                                border: 'none',
                                                                                borderRadius: '4px',
                                                                                cursor: 'pointer',
                                                                                fontSize: '11px'
                                                                            }}
                                                                        >
                                                                            Clear All
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {hodAssignments.length === 0 ? (
                                                                    <span style={{ fontSize: '11px', color: '#4CAF50', fontStyle: 'italic' }}>
                                                                        Super HOD - Can view all subjects
                                                                    </span>
                                                                ) : (
                                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                                        {hodAssignments.map(assignment => (
                                                                            <span
                                                                                key={assignment.id}
                                                                                style={{
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '6px',
                                                                                    padding: '4px 8px',
                                                                                    backgroundColor: '#e8f5e9',
                                                                                    borderRadius: '4px',
                                                                                    fontSize: '11px'
                                                                                }}
                                                                            >
                                                                                Gr{assignment.grade} {assignment.subject_name}
                                                                                <button
                                                                                    onClick={() => handleRemoveHodAssignment(assignment.id)}
                                                                                    style={{
                                                                                        padding: '0 4px',
                                                                                        backgroundColor: 'transparent',
                                                                                        color: '#c62828',
                                                                                        border: 'none',
                                                                                        cursor: 'pointer',
                                                                                        fontSize: '14px',
                                                                                        lineHeight: 1
                                                                                    }}
                                                                                >
                                                                                    x
                                                                                </button>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: isMobile ? '30px' : '60px', textAlign: 'center', color: '#999', fontSize: isMobile ? '12px' : '14px' }}>
                                Select a school to view details
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
